/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Outlet, useNavigate, useOutlet } from "react-router-dom";
import classNames from "classnames";

import AdsBanner, { BannerPosition } from "@components/ads-banner/ads-banner";
import Attempts from "@components/attempts/attempts";
import {
  Board,
  KeyboardHandle,
  onKeyPressEvent,
} from "@components/board/board";
import { CharacterHandle } from "@components/character/character";
import Coins from "@components/coins/coins";
import { useRemoveAdsPopup } from "@components/content-remove-ads/hooks/use-remove-ads-popup";
import EmptyAnimationDiv from "@components/empty-animation-div/empty-animation-div";
import EmptyQueryHandler from "@components/empty-query-handler/empty-query-handler";
import ExerciseTextOutput from "@components/exercise-text-output/exercise-text-output";
import InputAdvice from "@components/input-advice/input-advice";
import { useExercisePassed } from "@components/input-field/hooks/use-exercise-passed";
import { useTimeTracker } from "@components/input-field/hooks/use-time-tracker";
import { IPassedExerciseMetrics } from "@components/input-field/types/input-field.types";
import MainModal from "@components/modal/main-modal";
import ProgressPanel from "@components/progress-panel/progress-panel";
import RemoveAdsLink from "@components/remove-ads-link/remove-ads-link";
import WrapperAdsBanner from "@components/wrapper-ads-banner/WrapperAdsBanner";
import WrapperHelperModals, {
  detectModalType,
} from "@components/wrapper-helper-modals/wrapper-helper-modals";
import WrapperRewardsModals from "@components/wrapper-rewards-modals/wrapper-rewards-modals";

import { useDetectDevice } from "@hooks/use-detect-device";
import { useMediaQuery } from "@hooks/use-media-query";
import { useAppDispatch, useAppSelector } from "@hooks/use-store";
import {
  currentCoinsUpload,
  errorKeyUpload,
  progressUpload,
  setIsAdsScriptsReloaded,
} from "@store/app/app.slice";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import {
  getStoredRewardIds,
  isNotSpace,
  rewardsModalContent,
  updateStoredRewardIds,
} from "@helpers/helpers";
import { HelperModalType, IReward } from "@typings/global.types";

import { IGetExerciseEntrypointApiResponse } from "../../api/exercise-entrypoint/exercise-entrypoint.types";
import { exercisesApi } from "../../api/exercises";
import { userInfoApi } from "../../api/user-info";

type PropsType = {
  data: IGetExerciseEntrypointApiResponse | undefined;
};

function InputField({ data }: PropsType) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const { incrementPassedExercisesCounter } = useRemoveAdsPopup();
  const isMdScreen = useMediaQuery({ minWidth: "md" });
  const { isTouchDevice } = useDetectDevice();

  const { unAuthFirstString, speedRatio, currentCoins, keyboardLayout } =
    useAppSelector((state) => state.app);

  const { startTimer, stopTimer, time, resetTimer } = useTimeTracker();
  const { finishExercise } = useExercisePassed();
  const isAuthorized = Boolean(data);
  const typingText = data?.exercise.string ?? unAuthFirstString;
  const attempts = data?.user.numAvailableExercises ?? 0;
  const shouldShowAttempts =
    !data?.user.plus &&
    !window.reactApp.haveLicence &&
    window.reactApp.inClass === "None";

  const [helperModalType, setHelperModalType] =
    useState<HelperModalType | null>(null);
  const [typingTextArr, setTypingTextArr] = useState(typingText.split(""));
  const [speed, setSpeed] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [errorsCount, setErrorsCount] = useState(0);
  const [inputAdviceAnimation, setInputAdviceAnimation] =
    useState<boolean>(false);
  const [isProgressOverlayVisible, setIsProgressOverlayVisible] =
    useState<boolean>(!isMdScreen);

  const [rewardModalQueue, setRewardModalQueue] = useState<number[]>([]);
  const [currentModalType, setCurrentModalType] = useState<number | null>(null);
  const [showRewardsModal, setShowRewardsModal] = useState<boolean>(false);

  const characterRef = useRef<CharacterHandle>(null);
  const keyboardRef = useRef<KeyboardHandle>(null);
  const mobileErrorTimeout = useRef<
    ReturnType<typeof setTimeout> | undefined
  >();

  const [isExerciseFinished, setIsExerciseFinished] = useState<boolean>(false);
  const [nextLetter, setNextLetter] = useState<string>(typingTextArr[0]);
  const handleKeyPress = (event: onKeyPressEvent) => {
    if (!event.data || event.isCorrectDeadSymbol) return;
    const isStartExerciseStarted = typingTextArr.length !== typingText.length;
    startTimer();

    const modalType = detectModalType(
      event,
      typingTextArr[0],
      keyboardLayout,
      isStartExerciseStarted,
    );

    if (modalType) {
      setHelperModalType(modalType);
      dispatch(errorKeyUpload(""));
      stopTimer();
      return;
    }

    if (event.isCorrect) {
      if (isNotSpace(event.data)) {
        dispatch(currentCoinsUpload(currentCoins + 1));
        characterRef.current?.bite();
      } else {
        characterRef.current?.jump();
      }

      setCorrectCount((prevState) => prevState + 1);
      const [, ...newTypingText] = typingTextArr;
      setTypingTextArr(newTypingText);
      dispatch(
        progressUpload(
          ((typingText.length - newTypingText.length) / typingText.length) *
            100,
        ),
      );

      if (newTypingText.length === 0) {
        stopTimer();
        setIsExerciseFinished(true);
      }
    } else {
      characterRef.current?.error();
      setErrorsCount((prevState) => prevState + 1);
      dispatch(errorKeyUpload(event.data));
    }
  };

  const handleKeyUp = () => {
    if (!isMdScreen || !isTouchDevice) {
      clearTimeout(mobileErrorTimeout.current);
      mobileErrorTimeout.current = setTimeout(() => {
        dispatch(errorKeyUpload(""));
      }, 150);
    } else {
      dispatch(errorKeyUpload(""));
    }

    if (typingTextArr.length && typingTextArr[0] !== nextLetter) {
      setNextLetter(typingTextArr[0]);
    }
  };

  const resetExercise = useCallback(() => {
    setSpeed(0);
    setCorrectCount(0);
    setErrorsCount(0);
    setTypingTextArr(typingText.split(""));
    dispatch(progressUpload(0));
    dispatch(currentCoinsUpload(0));
    setNextLetter(typingText[0]);
  }, [typingText, dispatch]);

  const handleReload = () => {
    setTimeout(() => {
      resetExercise();
    }, 500);
    keyboardRef.current?.focus();
    resetTimer();
  };

  const focusOnInput = () => {
    setTimeout(() => {
      if (isMdScreen) {
        keyboardRef.current?.focus();
      } else {
        setIsProgressOverlayVisible(true);
      }
    }, 300);
  };

  useEffect(() => {
    if (data?.user.rewards) {
      const storedRewardIds = getStoredRewardIds(data.user.id);
      const currentRewardIds = data.user.rewards.map(
        (reward: IReward) => reward.id,
      );

      if (!storedRewardIds) {
        updateStoredRewardIds(currentRewardIds, data.user.id);
        return;
      }

      const newRewardIds = currentRewardIds.filter(
        (id) => !storedRewardIds.includes(id),
      );

      if (newRewardIds.length > 0) {
        updateStoredRewardIds(
          [...storedRewardIds, ...newRewardIds],
          data.user.id,
        );
      }

      const validRewardIds = newRewardIds.filter(
        (id) => rewardsModalContent[id as keyof typeof rewardsModalContent],
      );

      if (validRewardIds.length > 0) {
        setShowRewardsModal(true);
      }
      setRewardModalQueue(validRewardIds);
    }
  }, [data]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (rewardModalQueue.length > 0 && currentModalType === null) {
      const timer = setTimeout(() => {
        setCurrentModalType(rewardModalQueue[0]);
        setRewardModalQueue((prevQueue) => prevQueue.slice(1));
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [rewardModalQueue, currentModalType]);

  const handleCloseRewardModal = () => {
    if (rewardModalQueue.length > 0) {
      setCurrentModalType(rewardModalQueue[0]);
      setRewardModalQueue((prevQueue) => prevQueue.slice(1));
    } else {
      setCurrentModalType(null);
    }
  };

  useEffect(() => {
    const handleKeyDownStart = (e: KeyboardEvent) => {
      if (e.key === "Enter" && isTouchDevice) {
        keyboardRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDownStart);

    return () => window.removeEventListener("keydown", handleKeyDownStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isShowKeyboard = !isAuthorized || data?.user.showKeyboard;
  const isShowKeyboardHands = typingText.length === typingTextArr.length;

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(setIsAdsScriptsReloaded(true));
    }, 500);

    return () => clearTimeout(timeout);
  }, [dispatch]);

  useEffect(() => {
    if (
      !isExerciseFinished ||
      (isAuthorized && !data?.user.currentExerciseId)
    ) {
      return;
    }
    const metrics: IPassedExerciseMetrics = {
      isAuthorized,
      exerciseId: data?.user.currentExerciseId ?? 0,
      correctLettersCount: correctCount,
      mistakeLettersCount: errorsCount,
      speedRatio,
      duration: time,
      gameMode: data?.user.gameMode ?? false,
      coinsBeforeStart: data?.user.coins ?? 0,
    };

    finishExercise(metrics).then((redirectUrl) => {
      dispatch(exercisesApi.util.resetApiState());
      dispatch(userInfoApi.util.resetApiState());
      resetExercise();
      incrementPassedExercisesCounter(data?.user.serverSessionIndex ?? 0);
      navigate(redirectUrl);
    });
    setIsExerciseFinished(false);
  }, [
    incrementPassedExercisesCounter,
    data,
    dispatch,
    resetExercise,
    correctCount,
    errorsCount,
    finishExercise,
    isAuthorized,
    isExerciseFinished,
    navigate,
    speed,
    speedRatio,
    time,
  ]);

  useEffect(() => {
    if (outlet && !isAuthorized) {
      navigate(formatUrlWithLocale("/typing-tutor/"));
    }
  }, [isAuthorized, navigate, outlet]);

  useEffect(() => {
    if (isTouchDevice) {
      setIsProgressOverlayVisible(true);
    }
  }, [isTouchDevice]);

  useEffect(() => {
    setInputAdviceAnimation(true);
  }, []);

  const { checkPopup } = useRemoveAdsPopup();
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (data?.user.plus || window.reactApp.haveLicence) return;
      if (checkPopup(data?.user.serverSessionIndex ?? 0, showRewardsModal)) {
        setHelperModalType(HelperModalType.removeAds);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [
    checkPopup,
    data?.user.plus,
    data?.user.serverSessionIndex,
    showRewardsModal,
  ]);

  const onClickHandler = () => {
    document.cookie = `eventCategory=RemoveAds_btn_tutor_process; path=/;`;
  };

  return (
    <EmptyQueryHandler>
      <ExerciseTextOutput
        type={isMdScreen ? "desktop" : "mobile"}
        text={typingText}
        data={data}
        currentLetterIndex={typingText.length - typingTextArr.length}
        characterRef={characterRef}
        isOverlayVisible={isProgressOverlayVisible}
        isTooltipVisible={outlet === null}
        focusFunction={() => keyboardRef.current?.focus()}
      />
      <div className='relative mx-auto max-w-screen-container-xl'>
        {isAuthorized ? (
          <>
            <ProgressPanel
              onClickSettings={() =>
                navigate(formatUrlWithLocale("/typing-tutor/settings/"))
              }
              onClickReload={handleReload}
            />
            <EmptyAnimationDiv animation={inputAdviceAnimation}>
              {currentCoins === 0 ? (
                <InputAdvice />
              ) : (
                // eslint-disable-next-line
                <div
                  className={classNames("flex justify-center gap-x-4", {
                    "pb-4 md:pb-5": data?.user.gameMode || shouldShowAttempts,
                  })}
                >
                  {data?.user.gameMode ? (
                    <Coins
                      coins={data.user.coins}
                      withSpace={false}
                      currentCoins={currentCoins}
                    />
                  ) : (
                    <div />
                  )}
                  {shouldShowAttempts ? <Attempts attempts={attempts} /> : null}
                </div>
              )}
            </EmptyAnimationDiv>
          </>
        ) : (
          <div className='mb-3 h-[40px] md:mb-4' />
        )}
        <Board
          ref={keyboardRef}
          className={classNames({
            "md:h-[250px]": isShowKeyboard,
          })}
          showUI={isShowKeyboard && isMdScreen}
          shouldFocus={isMdScreen}
          showHands={isShowKeyboardHands}
          currentSymbol={typingTextArr[0]}
          keyboardLayout={keyboardLayout}
          onKeyPress={handleKeyPress}
          onKeyUp={handleKeyUp}
          onFocus={() => {
            if (!helperModalType) setIsProgressOverlayVisible(false);
            dispatch(errorKeyUpload(""));
          }}
          onBlur={() => {
            dispatch(errorKeyUpload(""));
            if (!helperModalType) setIsProgressOverlayVisible(true);
            stopTimer();
          }}
          onClickReload={handleReload}
        />

        {isAuthorized && data?.user.showKeyboard ? (
          <WrapperAdsBanner page='main-keyboard'>
            {/* <div className='min-h-[490px] md:min-h-[160px]'>
              <AdsBanner bannerPosition={BannerPosition.MAIN_BOTTOM} />
            </div> */}
            <AdsBanner bannerPosition={BannerPosition.MAIN_LEFT} />
            <AdsBanner bannerPosition={BannerPosition.MAIN_RIGHT} />
          </WrapperAdsBanner>
        ) : (
          <WrapperAdsBanner
            page={isAuthorized ? "main-without-keyboard" : "test-string"}
          >
            <div style={{ minHeight: "160px" }}>
              <AdsBanner
                bannerPosition={
                  isAuthorized
                    ? BannerPosition.MAIN_FULL_WIDTH
                    : BannerPosition.TEST_STRING
                }
              />
            </div>
          </WrapperAdsBanner>
        )}
        {isAuthorized && !data?.user.plus && !window.reactApp.haveLicence && (
          <div className='text-center md:mt-7'>
            {data?.user.isPromotion ? (
              <a
                className='not-prose hover-trigger-btn mx-auto flex w-fit'
                href={formatUrlWithLocale("/ratatype-plus/")}
                id='RemoveAds_btn_tutor_process'
                onClick={onClickHandler}
              >
                <div className='btn btn-quinary btn-with-icon w-fit'>
                  <img
                    className='mr-1'
                    src='/static/img/plus.svg'
                    alt='Remove ads'
                    width='24'
                    height='24'
                  />
                  <FormattedMessage id='remove-ads' />
                </div>
                <img
                  className='img-promotion ml-1'
                  src='/static/i/black-friday/button.png'
                  alt='Remove ads'
                  width='50'
                  height='50'
                />
              </a>
            ) : window.reactApp.isBirthDay ? (
              <div className='mx-auto w-fit'>
                <RemoveAdsLink
                  id='RemoveAds_btn_tutor_process'
                  translationID='remove-ads'
                />
                <img
                  className='mx-auto w-3/5'
                  src='/static/img/birthday/2025/flag-btn.svg'
                  alt='birthday'
                />
              </div>
            ) : (
              <RemoveAdsLink
                id='RemoveAds_btn_tutor_process'
                translationID='remove-ads'
              />
            )}
          </div>
        )}
      </div>
      <MainModal
        show={outlet !== null}
        onClick={() => navigate(formatUrlWithLocale("/typing-tutor/"))}
        showCloseBtn
        onEnter={() => setIsProgressOverlayVisible(false)}
        onExit={focusOnInput}
      >
        <Outlet
          context={{
            closeModal: () => navigate(formatUrlWithLocale("/typing-tutor/")),
            resetExercise,
          }}
        />
      </MainModal>
      <WrapperHelperModals
        isAuthorized={isAuthorized}
        onExit={focusOnInput}
        closeModalHandler={() => setHelperModalType(null)}
        modalType={helperModalType}
        isPromotion={data?.user.isPromotion}
      />
      {data?.user.rewards && (
        <WrapperRewardsModals
          closeModalHandler={handleCloseRewardModal}
          modalType={currentModalType}
          queueModal={rewardModalQueue.length}
        />
      )}
    </EmptyQueryHandler>
  );
}

export default InputField;
